import * as Apollo from '@apollo/client'
import {
  GQL_BoxesPageViewForActionsQuery,
  GQL_BoxesPageViewForActionsQueryVariables,
  GQL_ContractType,
  GQL_ProductStatus,
  useBoxesPageViewForActionsQuery,
} from '../../../generated/graphql'
import {BoxLite} from '../../../common/types/box'
import {CompartmentLite, CompartmentProduct} from '../../../common/types/compartment'
import {ProductStatus} from '../../../common/types/productStatus'
import {useEffect, useRef, useState} from 'react'

const PAGE_SIZE = 10000

const resolveLockedByDisplayName = (user: {id: string; email?: string | null; firstName?: string | null; lastName?: string | null}): string => {
  if (user.firstName || user.lastName) {
    return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
  }

  return user.email ?? user.id
}

const toDomainProductStatus = (status: GQL_ProductStatus | null | undefined): ProductStatus | undefined => {
  if (!status) return undefined

  switch (status) {
    case GQL_ProductStatus.Ok:
      return 'Ok'
    case GQL_ProductStatus.MissingParts:
      return 'MissingParts'
    case GQL_ProductStatus.Dirty:
      return 'Dirty'
    case GQL_ProductStatus.Broken:
      return 'Broken'
    case GQL_ProductStatus.Lost:
      return 'Lost'
    case GQL_ProductStatus.Other:
      return 'Other'
    default:
      return String(status) as ProductStatus
  }
}

type GQL_BoxRow = NonNullable<NonNullable<GQL_BoxesPageViewForActionsQuery['box']>['boxesPageView']>[number]

/**
 * boxesPageViewForActions intentionally queries a minimal subset of Box fields.
 *
 * This hook still returns BoxLite[] because downstream Actions code expects BoxLite,
 * but many BoxLite fields are NOT meaningful in Actions and are therefore stubbed here.
 *
 * If you ever need those fields in Actions, prefer expanding the GraphQL selection
 * rather than relying on these placeholders.
 */
const ACTIONS_BOX_LITE_STUB_FIELDS: Pick<
  BoxLite,
  'circuitBoardId' | 'serialNumber' | 'color' | 'initialActivationTime' | 'openStartTime' | 'openEndTime' | 'priceWeight' | 'sponsor'
> = {
  circuitBoardId: '',
  color: '#B40A7A',
  serialNumber: '',
  initialActivationTime: undefined,
  openStartTime: '00:00',
  openEndTime: '00:00',
  // This is a minimal placeholder; Actions does not use price weights.
  priceWeight: {
    id: 'actions-priceWeight',
    name: '',
    start: 0,
    firstHour: 0,
    secondHour: 0,
    thirdHour: 0,
    fourthHour: 0,
    fifthHour: 0,
    sixthHour: 0,
    penalty: 0,
  } as any,
  sponsor: undefined,
}

const mapPage = (page: GQL_BoxRow[]): BoxLite[] => {
  return page.map((b: GQL_BoxRow) => {
    const compartments: CompartmentLite[] = b.compartments.map((c) => {
      let product: CompartmentProduct | undefined

      if (c.product?.product) {
        product = {
          ...(c.product.product as any),
          productStatus: toDomainProductStatus(c.product.productStatus),
        } as CompartmentProduct
      }

      return {
        id: c.id,
        locked: Boolean(c.locked),
        private: Boolean(c.private),
        circuitBoardPort: c.circuitBoardPort,
        name: c.name ?? undefined,
        product,
        ongoingRental: c.ongoingRental ? {id: c.ongoingRental.id, rentStart: c.ongoingRental.rentStart ?? undefined} : undefined,
        latestLockEvent: c.latestLockEvent
          ? {
              id: c.latestLockEvent.id,
              date: c.latestLockEvent.date,
              lockedBy: resolveLockedByDisplayName(c.latestLockEvent.user),
            }
          : undefined,
      }
    })

    const location: BoxLite['location'] = {
      id: b.location.id,
      name: '',
      description: '',
      municipality: '',
      locality: '',
      coordinates: b.location.coordinates
        ? {
            lat: b.location.coordinates.lat,
            long: b.location.coordinates.long,
          }
        : undefined,
    }

    return {
      ...ACTIONS_BOX_LITE_STUB_FIELDS,

      id: b.id,
      active: Boolean(b.active),
      temporarilyClosed: b.temporarilyClosed,
      deprecated: Boolean(b.deprecated),
      name: b.name,
      contractType: (b.contractType ?? null) as GQL_ContractType | null,

      location,
      compartments,
    }
  })
}

export const useAllBoxesForActions = (
  baseOptions?: Apollo.QueryHookOptions<GQL_BoxesPageViewForActionsQuery, GQL_BoxesPageViewForActionsQueryVariables>,
) => {
  const [allBoxes, setAllBoxes] = useState<BoxLite[]>([])
  const [done, setDone] = useState(false)
  const [pageLoading, setPageLoading] = useState(false)

  const nextOffsetRef = useRef(0)
  const initializedRef = useRef(false)

  const {data, error, fetchMore} = useBoxesPageViewForActionsQuery({
    variables: {
      limit: PAGE_SIZE,
      offset: 0,
      onlyActive: true,
    },
    notifyOnNetworkStatusChange: true,
    ...baseOptions,
  })

  // Initialize from the first page once.
  useEffect(() => {
    if (initializedRef.current) return

    const firstPage = data?.box?.boxesPageView ?? null
    if (!firstPage) return

    initializedRef.current = true

    setAllBoxes(mapPage(firstPage))

    if (firstPage.length < PAGE_SIZE) {
      setDone(true)
      return
    }

    nextOffsetRef.current = PAGE_SIZE
  }, [data])

  // Fetch remaining pages sequentially.
  useEffect(() => {
    if (!initializedRef.current) return
    if (done) return
    if (pageLoading) return

    const offset = nextOffsetRef.current
    if (offset === 0) return

    setPageLoading(true)

    void fetchMore({
      variables: {
        limit: PAGE_SIZE,
        offset,
        onlyActive: true,
      },
    })
      .then((res) => {
        const page = res.data?.box?.boxesPageView ?? []

        if (page.length > 0) {
          setAllBoxes((prev) => [...prev, ...mapPage(page)])
        }

        if (page.length < PAGE_SIZE) {
          setDone(true)
        } else {
          nextOffsetRef.current = offset + PAGE_SIZE
        }

        setPageLoading(false)
      })
      .catch(() => {
        setDone(true)
        setPageLoading(false)
      })
  }, [done, pageLoading, fetchMore])

  return {
    data: allBoxes,
    loading: allBoxes.length === 0 && !done && !error,
    error,
  }
}
